import { useStore } from '@nanostores/react';
import { billing } from '@/services/billing';

export type BillingLoadState = {
  isLoading: boolean;
  hasError: boolean;
};

/**
 * Whether a billing tab can render its cards yet.
 *
 * The failure comes from the loader rather than from missing data. The shell starts the load with
 * `ignoreErrors`, so a failure leaves the service initialized with none of its data set — but so
 * does a successful load on an account with no effective subscription, because the Ordway and
 * Metronome paths always clear `billingInfo` and Ordway leaves `subscription` undefined. Reading
 * the absent data as a failure therefore replaced the tab with an error for the accounts that most
 * need it: one on Free, converting, or not yet provisioned, which reaches Billing details precisely
 * to add a card (SPE-4178).
 *
 * Shared because every tab built on the shell's load faces the same question, and a tab that
 * answered it differently would show cards built from half the billing data.
 */
export function useBillingLoadState(): BillingLoadState {
  const initialized = useStore(billing.initialized);
  const loadFailed = useStore(billing.loadFailed);

  return {
    isLoading: !initialized,
    hasError: initialized && loadFailed,
  };
}
